import React, { useEffect, useState, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import Button from '@mui/material/Button'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import Typography from '@mui/material/Typography'
import CheckboxInput from '../../../components/ui/inputs/CheckboxInput'
import Box from '@mui/material/Box'
import { Link } from 'react-router-dom'
import { styled } from '@mui/material/styles'
import SecurityIcon from '@mui/icons-material/Security'
import WarningIcon from '@mui/icons-material/Warning'
import FactCheckOutlinedIcon from '@mui/icons-material/FactCheckOutlined'
import { useUpdateConsultationMutation } from '@/core/services/consultation'
import { Consultation, UpdateConsultationDto } from '@/core/models/consultation.model'
import { ETH03_ASSESSMENT_VERSION } from './professional-consent/useProfessionalConsent'

export const localeWithMandatoryConsent = ['fr', 'it']

const CONSENT_STORAGE_PREFIX = 'patient_consent_'

// Types
export type PatientConsentDialogProps = {
  consultationId?: string
  // Group flow: one read-out consent covers every patient present, so apply it to all siblings
  consultationIds?: string[]
  onClose?: (accepted?: boolean) => void
  open: boolean
  professionalConsent?: Consultation['professionalConsent']
  showTreatingPhysician?: boolean
}

type Step = 'security' | 'warning' | 'understanding' | 'consent'

// Styled components
const StyledDialogContent = styled(DialogContent)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(2),
  minHeight: '400px',
  paddingTop: theme.spacing(2),
}))

const IconWrapper = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'center',
  marginBottom: theme.spacing(2),
}))

export function PatientConsentDialog(props: PatientConsentDialogProps) {
  const { consultationId, consultationIds, onClose, open, professionalConsent, showTreatingPhysician = true } = props
  const { t } = useTranslation()
  const [updateConsultation] = useUpdateConsultationMutation()
  const shouldShowUnderstandingStep = !professionalConsent?.teleconsultationUnderstandingAssessed

  const targetConsultationIds = consultationIds?.length ? consultationIds : consultationId ? [consultationId] : []

  // Check if consent was previously given for this consultation
  const hasStoredConsent = (consultationId: string): boolean => {
    return localStorage.getItem(`${CONSENT_STORAGE_PREFIX}${consultationId}`) === 'true'
  }

  // Store consent for this consultation (all sibling consultations in the group flow)
  const storeConsent = (consultationIdsToStore: string[]) => {
    for (const id of consultationIdsToStore) {
      localStorage.setItem(`${CONSENT_STORAGE_PREFIX}${id}`, 'true')
      const changes: UpdateConsultationDto = {}

      if (consents.recordingApproved) {
        changes.autoRecording = true
      }

      if (shouldShowUnderstandingStep) {
        const now = new Date().toISOString()
        changes.professionalConsent = {
          ...(professionalConsent ?? {}),
          teleconsultationUnderstandingAssessed: true,
          teleconsultationUnderstandingAssessedAt: now,
          teleconsultationUnderstandingAssessmentVersion: ETH03_ASSESSMENT_VERSION,
        }
      }

      if (Object.keys(changes).length > 0) {
        updateConsultation({
          id,
          changes,
        })
      }
    }
  }

  // Effect to check stored consent when dialog opens
  useEffect(() => {
    if (open && consultationId && hasStoredConsent(consultationId) && !shouldShowUnderstandingStep) {
      // If consent was already given, close the dialog
      onClose?.(true)
    }
  }, [open, consultationId, shouldShowUnderstandingStep])

  // Reset states when dialog opens
  useEffect(() => {
    if (open) {
      setCurrentStep('security')
      setConsents({
        declaration: false,
        participation: false,
        recordingApproved: false,
        treatingPhysician: showTreatingPhysician ? false : true,
        patientUnderstanding: !shouldShowUnderstandingStep,
      })
    }
  }, [open, shouldShowUnderstandingStep, showTreatingPhysician])

  const [currentStep, setCurrentStep] = useState<Step>('security')
  const [consents, setConsents] = useState({
    declaration: false,
    participation: false,
    treatingPhysician: showTreatingPhysician ? false : true,
    recordingApproved: false,
    patientUnderstanding: !shouldShowUnderstandingStep,
  })

  const handleClose = (event: {}, reason: string) => {
    if (reason === 'escapeKeyDown' || reason === 'backdropClick') return
    onClose?.(false)
  }

  const handleContinue = () => {
    switch (currentStep) {
      case 'security':
        setCurrentStep('warning')
        break
      case 'warning':
        setCurrentStep(shouldShowUnderstandingStep ? 'understanding' : 'consent')
        break
      case 'understanding':
        if (consents.patientUnderstanding) {
          setCurrentStep('consent')
        }
        break
      case 'consent':
        if (consents.declaration && consents.participation) {
          // Store consent when user accepts
          if (targetConsultationIds.length) {
            storeConsent(targetConsultationIds)
          }
          onClose?.(true)
        }
        break
    }
  }

  const handleCancel = () => {
    onClose?.(false)
  }

  const handleConsentChange = (type: keyof typeof consents) => (event: React.ChangeEvent<HTMLInputElement>) => {
    setConsents((prev) => ({
      ...prev,
      [type]: event.target.checked,
    }))
  }

  const renderContent = () => {
    switch (currentStep) {
      case 'security':
        return (
          <>
            <IconWrapper>
              <SecurityIcon sx={{ fontSize: 80 }} />
            </IconWrapper>
            <Typography>{t('dialog.teleConsent.steps.security.securityInfo')}</Typography>
            <Typography>{t('dialog.teleConsent.steps.security.securityGuarantee')}</Typography>
            <Typography>• {t('dialog.teleConsent.steps.security.dataProtection')}</Typography>
            <Typography>• {t('dialog.teleConsent.steps.security.videoProtection')}</Typography>
            <Typography>
              {t('dialog.teleConsent.steps.security.moreInfo')}{' '}
              <Link to="https://www.promotal-medconnect.com/privacy-policy" target="_blank" rel="noopener noreferrer">
                {t('dialog.teleConsent.steps.security.eula')}
              </Link>
            </Typography>
          </>
        )
      case 'warning':
        return (
          <>
            <IconWrapper>
              <WarningIcon sx={{ fontSize: 80 }} />
            </IconWrapper>
            <Typography variant="h6" color="error">
              {t('dialog.teleConsent.steps.warning.emergencyWarning')}
            </Typography>
            <Typography>{t('dialog.teleConsent.steps.warning.emergencyInstruction')}</Typography>
            <Typography>{t('dialog.teleConsent.steps.warning.serviceDescription')}</Typography>
            <Typography>{t('dialog.teleConsent.steps.warning.patientRights')}</Typography>
          </>
        )
      case 'consent':
        return (
          <>
            <Typography color="primary">{t('dialog.teleConsent.steps.consent.instruction')}</Typography>
            <CheckboxInput
              checked={consents.declaration}
              onChange={handleConsentChange('declaration')}
              label={t('dialog.teleConsent.steps.consent.declarationConsent')}
            />
            <CheckboxInput
              checked={consents.participation}
              onChange={handleConsentChange('participation')}
              label={t('dialog.teleConsent.steps.consent.participationConsent')}
            />
            <CheckboxInput
              checked={consents.recordingApproved}
              onChange={handleConsentChange('recordingApproved')}
              label={t('dialog.teleConsent.steps.consent.recordingApproved')}
            />
            {showTreatingPhysician && (
              <CheckboxInput
                checked={consents.treatingPhysician}
                onChange={handleConsentChange('treatingPhysician')}
                label={t('dialog.teleConsent.steps.consent.treatingPhysician')}
              />
            )}
          </>
        )
      case 'understanding':
        return (
          <>
            <IconWrapper>
              <FactCheckOutlinedIcon sx={{ fontSize: 80 }} />
            </IconWrapper>
            <Typography>{t('dialog.teleConsent.steps.understanding.instruction')}</Typography>
            <Typography variant="subtitle2" fontWeight={700}>
              {t('dialog.teleConsent.steps.understanding.questionsTitle')}
            </Typography>
            <Box component="ul" sx={{ m: 0, pl: 3 }}>
              <Typography component="li">{t('dialog.teleConsent.steps.understanding.question1')}</Typography>
              <Typography component="li">{t('dialog.teleConsent.steps.understanding.question2')}</Typography>
              <Typography component="li">{t('dialog.teleConsent.steps.understanding.question3')}</Typography>
              <Typography component="li">{t('dialog.teleConsent.steps.understanding.question4')}</Typography>
            </Box>
            <CheckboxInput
              checked={consents.patientUnderstanding}
              onChange={handleConsentChange('patientUnderstanding')}
              label={t('dialog.teleConsent.steps.understanding.confirmation')}
            />
          </>
        )
    }
  }

  const isContinueDisabled =
    (currentStep === 'understanding' && !consents.patientUnderstanding) ||
    (currentStep === 'consent' && (!consents.declaration || !consents.participation || !consents.treatingPhysician))

  return (
    <Dialog disableEscapeKeyDown open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {t(`dialog.teleConsent.steps.${currentStep}.title`)}
        <Typography variant="subtitle1" color="textSecondary">
          {t(`dialog.teleConsent.steps.${currentStep}.subtitle`)}
        </Typography>
      </DialogTitle>
      <StyledDialogContent>{renderContent()}</StyledDialogContent>
      <DialogActions>
        <Button onClick={handleCancel} color="primary">
          {t('dialog.teleConsent.buttons.cancel')}
        </Button>
        <Button onClick={handleContinue} color="primary" variant="contained" disabled={isContinueDisabled}>
          {t('dialog.teleConsent.buttons.continue')}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default PatientConsentDialog
